// Global batch polling service that persists across component unmounts
// This allows the topbar indicator to continue updating even when the modal is closed

type BatchStatusCallback = (status: any) => void
type BatchCompleteCallback = () => void

interface ActiveBatch {
  batchId: string
  interval: NodeJS.Timeout
  onStatusUpdate?: BatchStatusCallback
  onComplete?: BatchCompleteCallback
}

class BatchPollingService {
  private activeBatches: Map<string, ActiveBatch> = new Map()

  startPolling(
    batchId: string,
    getBatchStatus: (id: string) => Promise<any>,
    onStatusUpdate?: BatchStatusCallback,
    onComplete?: BatchCompleteCallback,
    pollInterval: number = 2000
  ) {
    // Clear any existing polling for this batch
    this.stopPolling(batchId)

    const interval = setInterval(async () => {
      try {
        const response = await getBatchStatus(batchId)
        const status = response.data?.status || response.status

        // Call status update callback
        if (onStatusUpdate) {
          onStatusUpdate(response.data || response)
        }

        // Check if batch is complete
        if (status === 'completed' || status === 'failed' || status === 'cancelled' || status === 'partially_completed') {
          this.stopPolling(batchId)
          if (onComplete) {
            onComplete()
          }
        }
      } catch (error) {
        console.error('Error polling batch status:', error)
        // Don't stop polling on error - might be temporary network issue
      }
    }, pollInterval)

    this.activeBatches.set(batchId, {
      batchId,
      interval,
      onStatusUpdate,
      onComplete
    })
  }

  stopPolling(batchId: string) {
    const batch = this.activeBatches.get(batchId)
    if (batch) {
      clearInterval(batch.interval)
      this.activeBatches.delete(batchId)
    }
  }

  isPolling(batchId: string): boolean {
    return this.activeBatches.has(batchId)
  }

  stopAll() {
    this.activeBatches.forEach((batch) => {
      clearInterval(batch.interval)
    })
    this.activeBatches.clear()
  }
}

// Export singleton instance
export const batchPollingService = new BatchPollingService()

